import React, { useState, useEffect } from 'react';
import { Drawer } from 'antd';
import { format, parseISO } from 'date-fns';
import { pl } from 'date-fns/locale';
import { fetchRecentLeagueResults } from '../../api/api';
import styles from './RecentResultsDrawer.module.css';

function formatMatchDate(resultDate) {
    if (!resultDate) return '';
    try {
        const date = typeof resultDate === 'string' ? parseISO(resultDate) : resultDate;
        return format(date, 'd MMMM', { locale: pl });
    } catch {
        return resultDate;
    }
}

function formatSetScores(setResults) {
    if (!setResults || setResults.length === 0) return '';
    return setResults
        .filter(s => !(s.playerOneGames === 0 && s.playerTwoGames === 0))
        .map(s => {
            const hasTiebreakPoints = s.isTiebreak &&
                (s.playerOneTiebreakPoints || s.playerTwoTiebreakPoints);
            const base = `${s.playerOneGames}:${s.playerTwoGames}`;
            if (hasTiebreakPoints) {
                return `${base} (${s.playerOneTiebreakPoints}-${s.playerTwoTiebreakPoints})`;
            }
            return base;
        })
        .join(' ');
}

function formatTeam(firstName, lastName, partnerFirstName, partnerLastName) {
    const main = `${lastName} ${firstName}`.trim();
    if (partnerFirstName || partnerLastName) {
        const partner = `${partnerLastName} ${partnerFirstName}`.trim();
        return `${main} / ${partner}`;
    }
    return main;
}

function RecentResultRow({ match }) {
    const meta = [
        match.group ? `Grupa ${match.group}` : null,
        match.leagueRoundNumber ? `Runda ${match.leagueRoundNumber}` : null,
        formatMatchDate(match.resultDate),
    ].filter(Boolean).join(' · ');

    const team1 = formatTeam(
        match.participant1FirstName,
        match.participant1LastName,
        match.participant1PartnerFirstName,
        match.participant1PartnerLastName,
    );
    const team2 = formatTeam(
        match.participant2FirstName,
        match.participant2LastName,
        match.participant2PartnerFirstName,
        match.participant2PartnerLastName,
    );

    const scoreStr = formatSetScores(match.setResults);

    const p1IsWinner = match.winnerId != null && match.participant1Id != null
        ? Number(match.winnerId) === Number(match.participant1Id)
        : false;
    const p2IsWinner = match.winnerId != null && match.participant2Id != null
        ? Number(match.winnerId) === Number(match.participant2Id)
        : false;

    const p1Resigned = match.injuryResignation &&
        match.injuryResignationParticipantId &&
        match.participant1Id &&
        match.injuryResignationParticipantId === match.participant1Id;
    const p2Resigned = match.injuryResignation &&
        match.injuryResignationParticipantId &&
        match.participant2Id &&
        match.injuryResignationParticipantId === match.participant2Id;

    return (
        <div className={styles.matchRow}>
            <div className={styles.meta}>{meta}</div>
            <div className={styles.matchBody}>
                <div className={styles.teams}>
                    <div className={p1IsWinner ? styles.winner : styles.player}>
                        {team1}{p1Resigned ? ' (K)' : ''}
                    </div>
                    <div className={p2IsWinner ? styles.winner : styles.player}>
                        {team2}{p2Resigned ? ' (K)' : ''}
                    </div>
                </div>
                <div className={styles.score}>{scoreStr}</div>
            </div>
        </div>
    );
}

function RecentResultsDrawer({ open, onClose, tournamentId }) {
    const [results, setResults] = useState([]);
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        if (!open || !tournamentId) return;
        let cancelled = false;
        setIsLoading(true);
        fetchRecentLeagueResults(tournamentId, 6)
            .then(res => {
                if (cancelled) return;
                setResults(res.success ? res.data : []);
            })
            .catch(() => {
                if (!cancelled) setResults([]);
            })
            .finally(() => {
                if (!cancelled) setIsLoading(false);
            });
        return () => { cancelled = true; };
    }, [open, tournamentId]);

    return (
        <Drawer
            placement="bottom"
            title="Ostatnie wyniki"
            open={open}
            onClose={onClose}
            height="auto"
            headerStyle={{ maxWidth: 600, margin: '0 auto', width: '100%' }}
        >
            {isLoading && <div className={styles.loadingText}>Ładowanie...</div>}
            {!isLoading && results.length === 0 && (
                <div className={styles.emptyText}>Brak ostatnich wyników</div>
            )}
            {!isLoading && results.length > 0 && (
                <div className={styles.list}>
                    {results.map(m => (
                        <RecentResultRow key={m.matchId} match={m} />
                    ))}
                </div>
            )}
        </Drawer>
    );
}

export default RecentResultsDrawer;
